// src/components/business/BusinessLogo.tsx
//
// Logo do negócio (correção UX v2 — wireframe seção 04, .bizlogo/.logoup
// portados em src/styles/wf-biz.css). Tile com a imagem do negócio ou
// fallback de iniciais, + botão de upload (file picker → POST /v1/uploads →
// PATCH /v1/businesses/:id { logo_url }).

import { useRef, useState } from 'react';
import { api, type Business } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Loader2, Upload } from 'lucide-react';
import { businessInitials } from './business-constants';

interface BusinessLogoProps {
  business: Business;
  /** 'lg' = .wf-bizlogo.lg do wireframe (header da tela do negócio). */
  size?: 'md' | 'lg';
  className?: string;
}

/** Tile visual do logo (wireframe .bizlogo — imagem ou iniciais). */
export function BusinessLogo({ business, size = 'md', className }: BusinessLogoProps) {
  const [imgError, setImgError] = useState(false);
  const showImage = business.logo_url && !imgError;

  return (
    <div className={cn('wf-bizlogo', size === 'lg' && 'lg', className)}>
      {showImage ? (
        <img
          src={business.logo_url!}
          alt={`Logo de ${business.name}`}
          onError={() => setImgError(true)}
        />
      ) : (
        <span className="select-none">{businessInitials(business.name)}</span>
      )}
    </div>
  );
}

interface BusinessLogoUploadButtonProps {
  business: Business;
  /** Recebe o business atualizado após o PATCH com o novo logo_url. */
  onUpdated: (biz: Business) => void;
  onError?: (message: string) => void;
  className?: string;
}

/**
 * Botão de upload do logo (wireframe .logoup): clique → file picker →
 * POST /v1/uploads → api.updateBusiness(id, { logo_url }). stopPropagation
 * pra não disparar o clique do card.
 */
export function BusinessLogoUploadButton({
  business,
  onUpdated,
  onError,
  className,
}: BusinessLogoUploadButtonProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file: File) => {
    setUploading(true);
    try {
      // purpose:'asset' → signed URL de longa duração (~10 anos). O TTL default
      // de 1h matava o logo: a URL salva em businesses.logo_url expirava e a
      // imagem quebrava. Backend pré-restart ignora o campo (TTL 1h, degrade).
      const uploaded = await api.uploadFile(file, { purpose: 'asset' });
      const updated = await api.updateBusiness(business.id, { logo_url: uploaded.signed_url });
      onUpdated(updated);
    } catch (err) {
      onError?.(err instanceof Error ? err.message : 'Erro ao subir logo');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        aria-hidden="true"
        tabIndex={-1}
        // BIZ-01: o .click() programático borbulha — um ancestral com
        // preventDefault cancelaria a abertura do picker (caso do card).
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void handleFile(file);
        }}
      />
      <button
        type="button"
        title="Subir logo do negócio"
        aria-label="Subir logo do negócio"
        disabled={uploading}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          inputRef.current?.click();
        }}
        className={cn('wf-logoup', uploading && 'opacity-60 cursor-not-allowed', className)}
      >
        {uploading ? (
          <Loader2 className="ic animate-spin" aria-hidden="true" />
        ) : (
          <Upload className="ic" aria-hidden="true" />
        )}
      </button>
    </>
  );
}
